import { useState, useEffect } from "react";
import GlobalStyles from "../components/GlobalStyles";
import { C } from "../constants";
import { callClaude } from "../api";
import { buildEvalPrompt } from "../prompts";

export default function Evaluation({ cfg, msgs, onRestart }) {
  const [ev,      setEv]   = useState(null);
  const [loading, setLoad] = useState(true);

  useEffect(() => {
    const run = async () => {
      const transcript = msgs
        .filter(m => !m.isSystem)
        .map(m => {
          let sp = m.role === "user"
            ? "ABOGADO (USUARIO)"
            : m.isAiQuestion
              ? "ABOGADO (IA)"
              : m.witnessName || m.speaker?.toUpperCase() || "TESTIGO";
          return `${sp}: ${m.content}`;
        })
        .join("\n");
      try {
        const raw = await callClaude(
          [{ role:"user", content:`ROL: ${cfg.userRole === "fiscal" ? "Fiscal" : "Defensor"}\nCASO: ${cfg.caseData?.resumen}\n\nTRANSCRIPCIÓN COMPLETA:\n${transcript}` }],
          buildEvalPrompt(cfg.userRole),
          1400
        );
        setEv(JSON.parse(raw.replace(/```json|```/g, "").trim()));
      } catch {
        setEv({ puntaje_total:0, categorias:{}, fortalezas:[], areas_mejora:[], mejor_pregunta:"", peor_pregunta:"", recomendacion:"Error al generar la evaluación." });
      }
      setLoad(false);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return (
    <div style={{ height:"100vh", background:C.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
      <GlobalStyles/>
      <div style={{ width:32, height:32, border:`3px solid ${C.border}`, borderTopColor:C.ink, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
      <p style={{ color:C.textDim, fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:900, fontStyle:"italic" }}>El tribunal delibera…</p>
    </div>
  );

  const sc        = s => s >= 80 ? C.green : s >= 60 ? C.gold : C.red;
  const catLabels = {
    tecnica_interrogatorio: "Técnica de interrogatorio",
    coherencia_teoria:      "Coherencia con teoría",
    manejo_objeciones:      "Manejo de objeciones",
    lenguaje_juridico:      "Lenguaje jurídico",
  };

  return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"'Source Serif 4',serif", overflowY:"scroll" }}>
      <GlobalStyles/>

      {/* Header */}
      <div style={{ borderBottom:`2px solid ${C.ink}`, padding:"16px 32px", background:C.white, display:"flex", alignItems:"center", gap:16 }}>
        <span style={{ fontFamily:"'Playfair Display',serif", fontWeight:900, fontSize:22, color:C.ink }}>
          Litis<span style={{ color:C.gold }}>Lab</span>
        </span>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:16, fontFamily:"'Playfair Display',serif", fontWeight:900, color:C.ink }}>Evaluación Final</div>
          <div style={{ fontSize:12, color:C.textDim, fontWeight:700 }}>
            {cfg.userName} · {cfg.userRole === "fiscal" ? "Fiscal" : "Defensor"} · {cfg.caseData?.titulo}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:800, margin:"0 auto", padding:"40px 24px 80px" }}>

        {/* Score */}
        <div style={{ display:"flex", justifyContent:"center", marginBottom:40 }}>
          <div style={{ textAlign:"center", padding:"32px 56px", background:C.white, borderRadius:10, border:`2px solid ${C.ink}` }}>
            <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:C.textFaint, letterSpacing:3, textTransform:"uppercase", marginBottom:8, fontWeight:700 }}>Puntaje total</div>
            <div style={{ fontSize:88, fontFamily:"'Playfair Display',serif", fontWeight:900, color:sc(ev.puntaje_total), lineHeight:1 }}>{ev.puntaje_total}</div>
            <div style={{ fontSize:14, color:C.textDim, fontWeight:700 }}>de 100 puntos</div>
          </div>
        </div>

        {/* Categories */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
          {Object.entries(ev.categorias || {}).map(([k, cat]) => (
            <div key={k} style={{ background:C.white, borderRadius:8, border:`1px solid ${C.border}`, padding:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                <span style={{ fontSize:10, color:C.textDim, fontFamily:"'DM Mono',monospace", fontWeight:700, flex:1, marginRight:8, textTransform:"uppercase" }}>
                  {catLabels[k] || k}
                </span>
                <span style={{ fontSize:28, fontFamily:"'Playfair Display',serif", fontWeight:900, color:sc(cat.puntaje), flexShrink:0 }}>{cat.puntaje}</span>
              </div>
              <div style={{ height:4, background:C.panel, borderRadius:2, marginBottom:10 }}>
                <div style={{ height:"100%", width:`${cat.puntaje}%`, background:sc(cat.puntaje), borderRadius:2 }}/>
              </div>
              <p style={{ fontSize:13, color:C.textMid, lineHeight:1.6, fontWeight:700 }}>{cat.comentario}</p>
            </div>
          ))}
        </div>

        {/* Strengths & improvements */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
          <div style={{ background:C.greenBg, borderRadius:8, border:"1px solid #B8D4B8", padding:16 }}>
            <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:C.green, letterSpacing:2, textTransform:"uppercase", marginBottom:10, fontWeight:700 }}>✓ Fortalezas</div>
            {(ev.fortalezas || []).map((f, i) => (
              <div key={i} style={{ fontSize:13, color:C.textMid, marginBottom:8, paddingLeft:12, borderLeft:`3px solid ${C.green}`, fontWeight:700, lineHeight:1.5 }}>· {f}</div>
            ))}
          </div>
          <div style={{ background:C.redBg, borderRadius:8, border:"1px solid #D4B8B8", padding:16 }}>
            <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:C.red, letterSpacing:2, textTransform:"uppercase", marginBottom:10, fontWeight:700 }}>⚠ Áreas de mejora</div>
            {(ev.areas_mejora || []).map((a, i) => (
              <div key={i} style={{ fontSize:13, color:C.textMid, marginBottom:8, paddingLeft:12, borderLeft:`3px solid ${C.red}`, fontWeight:700, lineHeight:1.5 }}>· {a}</div>
            ))}
          </div>
        </div>

        {/* Best / worst question */}
        {(ev.mejor_pregunta || ev.peor_pregunta) && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
            {ev.mejor_pregunta && (
              <div style={{ background:C.greenBg, borderRadius:8, border:"1px solid #B8D4B8", padding:16 }}>
                <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:C.green, letterSpacing:2, textTransform:"uppercase", marginBottom:8, fontWeight:700 }}>★ Tu mejor pregunta</div>
                <p style={{ fontSize:13, color:C.textMid, fontStyle:"italic", fontWeight:700, lineHeight:1.6 }}>"{ev.mejor_pregunta}"</p>
              </div>
            )}
            {ev.peor_pregunta && (
              <div style={{ background:C.redBg, borderRadius:8, border:"1px solid #D4B8B8", padding:16 }}>
                <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:C.red, letterSpacing:2, textTransform:"uppercase", marginBottom:8, fontWeight:700 }}>✗ Tu pregunta más débil</div>
                <p style={{ fontSize:13, color:C.textMid, fontStyle:"italic", fontWeight:700, lineHeight:1.6 }}>"{ev.peor_pregunta}"</p>
              </div>
            )}
          </div>
        )}

        {/* Recommendation */}
        <div style={{ background:C.white, borderRadius:8, border:`2px solid ${C.ink}`, padding:22, marginBottom:32 }}>
          <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:C.textFaint, letterSpacing:2, textTransform:"uppercase", marginBottom:10, fontWeight:700 }}>Recomendación del tribunal</div>
          <p style={{ fontSize:16, color:C.textMid, lineHeight:1.8, fontStyle:"italic", fontWeight:700 }}>"{ev.recomendacion}"</p>
        </div>

        {/* Restart */}
        <div style={{ display:"flex", justifyContent:"center" }}>
          <button onClick={onRestart} style={{ padding:"14px 36px", borderRadius:8, background:C.ink, color:C.bg, fontSize:18, fontWeight:900, fontFamily:"'Playfair Display',serif" }}>
            ↩ Nueva simulación
          </button>
        </div>
      </div>
    </div>
  );
}
